/**
 * User Data Bootstrap
 *
 * Component that loads/manages user data on app boot (runs once).
 *
 * For authenticated users:
 * - Uses stale-while-revalidate pattern
 * - Returns cached data immediately (if exists)
 * - Fetches fresh data in background (if stale)
 *
 * For anonymous users:
 * - Loads or creates anonymous user with preferences
 * - Preserves local preferences for unauthenticated experience
 *
 * Note: This runs only once on app mount. Auth state changes
 * are handled by lifecycle handlers (login/logout).
 */

import { useEffect } from 'react'
import { authService } from '@/services/auth'
import { userManager } from '@/managers/userManager'

export function UserDataBootstrap() {
  useEffect(() => {
    const isAuthenticated = authService.isAuthenticated()

    if (isAuthenticated) {
      // Authenticated user - revalidate data from backend
      console.log('[UserDataBootstrap] Revalidating authenticated user data')

      userManager.revalidate().catch(error => {
        console.error('[UserDataBootstrap] Failed to revalidate user data:', error)
        // Don't throw - app can continue with cached or no data
      })
    } else {
      // Anonymous user - check if we have cached user
      console.log('[UserDataBootstrap] Checking anonymous user data')

      const cached = userManager.get()

      if (!cached) {
        // No cached user - create empty anonymous user
        console.log('[UserDataBootstrap] Creating empty anonymous user')
        userManager.setAnonymous({ modalities: [] })
      } else if (!userManager.isAnonymous()) {
        // Has cached authenticated user but not logged in anymore
        // Clear it and create anonymous
        console.log('[UserDataBootstrap] Clearing stale authenticated user, creating anonymous')
        userManager.clear()
        userManager.setAnonymous({ modalities: [] })
      } else {
        // Already has anonymous user cached
        console.log('[UserDataBootstrap] Anonymous user already cached')
      }
    }
  }, []) // Empty deps - runs once on mount

  return null // This component doesn't render anything
}
